/**
 * Smith Protective — Import History (Iter186).
 *
 * Every past prospect upload with its original file, counters, and quick
 * links to re-download the raw XLSX/CSV/PDF from Emergent Object Storage.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, RefreshCw, Download, FileSpreadsheet, FileText, File as FileIcon,
  Loader2, CheckCircle2, AlertTriangle, ArrowRight,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";

const iconFor = (filename) => {
  const f = (filename || "").toLowerCase();
  if (f.endsWith(".xlsx") || f.endsWith(".csv")) return FileSpreadsheet;
  if (f.endsWith(".pdf") || f.endsWith(".docx")) return FileText;
  return FileIcon;
};

const kb = (n) => (n < 1024 ? `${n}B` : n < 1024 * 1024
  ? `${(n / 1024).toFixed(1)}KB` : `${(n / 1024 / 1024).toFixed(2)}MB`);

export default function SmithImports() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/smith/import-batches`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar imports");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const download = async (batch) => {
    setDownloadingId(batch.id);
    try {
      const resp = await axios.get(
        `${API}/smith/import-batches/${batch.id}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = batch.filename || `${batch.id}.dat`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Baixado: ${batch.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Arquivo não disponível");
    } finally { setDownloadingId(null); }
  };

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-imports-header"
          title="Smith · Import History"
          subtitle={`${items.length} upload${items.length === 1 ? "" : "s"} · arquivos originais + contadores`}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#1E3A8A 0%,#0F172A 100%)", color: "#DC2626" }}>
              <Shield className="w-5 h-5" />
            </div>
          }
        >
          <Link to="/smith/prospects" data-testid="imports-prospects-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Prospects
          </Link>
          <Link to="/smith/contacts" data-testid="imports-contacts-nav"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-[#DC2626]">
            Emails
          </Link>
          <button type="button" onClick={load} disabled={loading}
            data-testid="imports-refresh"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </AppHeader>

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px] mx-auto space-y-4 pb-24 lg:pb-6"
              data-testid="smith-imports-page">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" />
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border p-10 text-center text-slate-400 text-[13px]"
                 style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}
                 data-testid="imports-empty">
              Nenhum import ainda. Suba uma planilha em <strong className="text-slate-200">Prospects → Import</strong>.
            </div>
          ) : (
            <div className="space-y-3" data-testid="imports-list">
              {items.map((b) => {
                const Icon = iconFor(b.filename);
                const total = (b.inserted || 0) + (b.updated || 0);
                return (
                  <div key={b.id}
                       data-testid={`import-batch-${b.id}`}
                       className="rounded-2xl border p-4"
                       style={{ background: "rgba(15,23,42,0.7)", borderColor: "rgba(220,38,38,0.20)" }}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                             style={{ background: "rgba(220,38,38,0.15)", color: "#DC2626" }}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13.5px] font-extrabold text-white truncate">
                            {b.filename || b.id}
                          </div>
                          <div className="text-[11px] text-slate-500 tabular-nums">
                            {b.uploaded_at ? new Date(b.uploaded_at).toLocaleString("pt-BR",
                              { dateStyle: "short", timeStyle: "short" }) : "—"}
                            {b.uploaded_by && <span> · {b.uploaded_by}</span>}
                            {b.size_bytes && <span> · {kb(b.size_bytes)}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link to={`/smith/prospects?batch=${b.id}`}
                              data-testid={`import-view-prospects-${b.id}`}
                              className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold hover:border-[#DC2626] inline-flex items-center gap-1.5">
                          Ver {total} prospect{total === 1 ? "" : "s"} <ArrowRight className="w-3 h-3" />
                        </Link>
                        {b.storage_path && (
                          <button type="button" onClick={() => download(b)}
                                  disabled={downloadingId === b.id}
                                  data-testid={`import-download-${b.id}`}
                                  className="h-9 px-3 rounded-lg font-bold text-[12px] text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                                  style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                            {downloadingId === b.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Download className="w-3.5 h-3.5" />}
                            Baixar
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2 text-[11.5px]">
                      <StatChip label="Novos" value={b.inserted} icon={CheckCircle2} color="#10B981" testid={`import-inserted-${b.id}`} />
                      <StatChip label="Atualizados" value={b.updated} icon={RefreshCw} color="#3B82F6" testid={`import-updated-${b.id}`} />
                      <StatChip label="Ignorados" value={b.skipped} icon={AlertTriangle} color="#F59E0B" testid={`import-skipped-${b.id}`} />
                      {b.errors?.length > 0 && (
                        <StatChip label="Erros" value={b.errors.length} icon={AlertTriangle} color="#EF4444" testid={`import-errors-${b.id}`} />
                      )}
                    </div>
                    {(b.errors || []).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-[10.5px] text-red-300 cursor-pointer font-bold uppercase tracking-wider">
                          {b.errors.length} erro(s) — clique para ver
                        </summary>
                        <ul className="mt-1 space-y-0.5 text-[11px] text-red-200 max-h-32 overflow-y-auto pl-3 list-disc">
                          {b.errors.slice(0, 15).map((err, i) => (
                            <li key={i}>{typeof err === "string" ? err : `Linha ${err.row}: ${err.msg}`}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function StatChip({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-black/25 border border-white/5" data-testid={testid}>
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <div className="min-w-0">
        <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="text-[14px] font-extrabold text-white tabular-nums">{value || 0}</div>
      </div>
    </div>
  );
}
